import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ProductShot } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { CATEGORIES, PRODUCTS, waLink } from "../lib/site";

const Chip = ({ children }) => (
  <span className="border border-white/30 bg-white/10 rounded-full px-3 py-1 text-[11px] font-medium text-white">{children}</span>
);

const DarkCard = ({ p, i }) => {
  return (
    <Reveal delay={(i % 2) * 0.06}>
      <article id={p.slug} data-testid={`product-card-${p.slug}`}
        className="group relative flex h-full flex-col overflow-hidden rounded-[22px] border border-line bg-[#344D5B] text-white p-3 transition-shadow duration-500 hover:shadow-[0_28px_60px_-30px_rgba(17,24,39,0.5)] scroll-mt-32">
        <div className="relative overflow-hidden rounded-[14px]">
          <ProductShot p={p} className="aspect-[4/3]" field scale={1} />
          <span className="absolute left-3 top-3 bg-black/55 text-white backdrop-blur px-3 py-1.5 rounded-full text-[11px] tracking-[0.2em]">
            {String(i + 1).padStart(2, "0")} — {p.category.toUpperCase()}
          </span>
          {i === 0 && <span className="absolute bottom-3 left-3 bg-brand text-white px-3 py-1 rounded-full text-xs font-semibold">Mais procurada</span>}
        </div>
        <div className="flex flex-1 flex-col px-3 pb-2 pt-5">
          <h3 className="font-display text-2xl font-bold tracking-tight leading-none">
            <span className="mr-2 text-base font-medium text-white/70">SOLLUS</span>{p.name}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm font-medium leading-relaxed text-white/90">{p.desc}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Chip>{p.model}</Chip>
          </div>
          <div className="mt-auto flex flex-col gap-2 pt-6">
            <a href={waLink(`Olá, SOLLUS! Gostaria de um orçamento para: ${p.model}.`)} target="_blank" rel="noreferrer"
              data-testid={`product-quote-btn-${p.slug}`}
              className="flex items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-base font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand-dark">
              Solicitar orçamento agora <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export default function Produtos() {
  const [cat, setCat] = useState("Todos");
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, [hash]);
  const list = cat === "Todos" ? PRODUCTS : PRODUCTS.filter((p) => p.category === cat);

  return (
    <div data-testid="produtos-page" className="pb-20">
      <div className="container-x grid items-end gap-8 pb-10 pt-12 lg:grid-cols-12 lg:pt-16" data-testid="produtos-head">
        <Reveal className="lg:col-span-7">
          <p className="eyebrow flex items-center gap-3"><span className="h-px w-8 bg-brand" />Catálogo</p>
          <h1 className="mt-4 font-display text-5xl font-black uppercase leading-[0.92] tracking-tight text-ink sm:text-6xl lg:text-7xl">
            Máquinas e<br /><span className="text-outline-dark">implementos</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <p className="text-base text-steel md:text-lg">Da cana-de-açúcar aos grãos. Filtre por categoria, abra a ficha técnica e peça o orçamento do modelo certo para a sua operação.</p>
        </Reveal>
      </div>

      <div className="sticky top-16 z-20 border-y border-line bg-white/90 py-3 backdrop-blur-xl lg:top-[72px]" data-testid="product-filters">
        <div className="container-x flex gap-2 overflow-x-auto no-scrollbar">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                cat === c ? "bg-brand text-white" : "border border-line bg-white text-ink hover:border-ink"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="container-x grid gap-6 pt-10 sm:grid-cols-2">
        {list.map((p, i) => <DarkCard key={p.slug} p={p} i={i} />)}
        <Reveal delay={0.06}>
          <article data-testid="pecas-card"
            className="flex h-full flex-col overflow-hidden rounded-[22px] border border-line bg-[#344D5B] text-white p-3 transition-shadow duration-500 hover:shadow-[0_28px_60px_-30px_rgba(17,24,39,0.5)]">
            <div className="relative overflow-hidden rounded-[14px]">
              <div className="aspect-[16/10]">
                <img src="/img/pecas.jpg" alt="Peças de reposição SOLLUS" loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
              <span className="absolute left-3 top-3 bg-black/55 text-white backdrop-blur px-3 py-1.5 rounded-full text-[11px] tracking-[0.2em]">
                REPOSIÇÃO
              </span>
            </div>
            <div className="flex flex-1 flex-col px-3 pb-2 pt-5">
              <h3 className="font-display text-2xl font-bold tracking-tight leading-none">
                <span className="mr-2 text-base font-medium text-white/70">SOLLUS</span>Peças
              </h3>
              <p className="mt-2 text-sm font-medium leading-relaxed text-white/90">
                Engrenagens, correntes, discos e componentes originais para manter sua máquina SOLLUS sempre rodando.
              </p>
              <div className="mt-auto flex flex-col gap-2 pt-6">
                <a href={waLink("Olá, SOLLUS! Preciso de peças de reposição.")} target="_blank" rel="noreferrer"
                  data-testid="pecas-whatsapp-btn"
                  className="flex items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-base font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand-dark">
                  Solicitar peças agora <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </article>
        </Reveal>
      </div>
    </div>
  );
}
